/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Wires Monaco's suggest-widget events to the completion telemetry emitters.
 *
 * Hooks into `editor.contrib.suggestController` via `getContribution`. Falls
 * back to no-op if the contribution isn't available (older Monaco versions,
 * custom builds, headless test environments). Telemetry is fire-and-forget so
 * any failure here is silently swallowed; the editor remains unaffected.
 *
 * Accepts are recorded from the controller's `onWillInsertSuggestItem`, the
 * one event every accept path fires (click, Enter, Tab, commit character)
 * and fires BEFORE the widget hides: Monaco's controller inserts, cancels
 * the session and hides the widget from inside its own select listener, and
 * Enter / Tab (`acceptSelectedSuggestion`) never fire the widget's
 * `onDidSelect` at all (1.5.1 fix pass 2 — a widget-select hook counted a
 * click as a dismissal AND an accept, and Enter / Tab as dismissals only).
 *
 * The orchestrator wiring in `SqlMonaco.tsx` calls `recordOrchestratorRun`
 * before each completion provider invocation so the accept event can compute
 * `latencyMs`, and `recordOrchestratorResolved` after it — whose
 * `positionKind` tags the accepts of the list it produced (the run starts
 * before the caret is classified, so its own meta is `{}`; 1.5.1 F4).
 * `recordOrchestratorError` is called from the same site's try/catch around
 * the orchestrator.
 */

import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { DatabaseEngineId } from '@/core/engines';
import type { CompletionPositionKind } from '@/core/engines/intellisenseTypes';
import type { PredicateHeadRole, ValueSlotRole } from '@/features/sql-grammar/model';

import {
  emitCompletionAccepted,
  emitCompletionOrchestratorError,
  type CompletionClassifiedBy,
  type CompletionDismissReason,
  type CompletionOrchestratorStage,
} from './completionTelemetry';
import {
  recordAccepted,
  recordDismissed,
  recordOrchestratorErrorCount,
  recordResolved,
  recordSuperseded,
} from './completionSessionAggregate';

/**
 * Per-app-run caps on the two remaining per-event completion streams
 * (2026-07-25 volume audit, risks #1/#3). Exact totals always continue into
 * `completion.session_summary` (`acceptedCount`, `error_<stage>`); only the
 * per-event DETAIL (rankIndex/tier/latency; stage arrival timing) is capped.
 *
 * - accepted: human-bounded, but a heavy author (1-2 accepts/min for hours)
 *   alone approaches the Umami instance's entire daily row baseline. 25
 *   detailed events per engine per run keep the rank/tier distribution
 *   readable without the volume.
 * - orchestrator.error: sits in the per-keystroke provider path — a
 *   deterministic resolver throw would emit 3-5 events/SECOND unsampled
 *   (~100-300k/day from one user). 5 per (engine, stage) per run is presence
 *   signal enough; the summary carries exact counts.
 */
export const MAX_ACCEPTED_EVENTS_PER_ENGINE_RUN = 25;
export const MAX_ERROR_EVENTS_PER_ENGINE_STAGE_RUN = 5;

const acceptedEmittedByEngine = new Map<string, number>();
const errorEmittedByEngineStage = new Map<string, number>();

/** Test-only: reset the per-app-run emission caps. */
export function _resetCompletionTelemetryRunCapsForTests(): void {
  acceptedEmittedByEngine.clear();
  errorEmittedByEngineStage.clear();
}

export type TelemetryDeps = {
  readonly track: TrackFunction;
  readonly getAnalyticsEnabled: () => boolean;
  readonly getEngineId: () => DatabaseEngineId;
  /**
   * Fired on every suggest-widget accept with the accepted item's label,
   * BEFORE the analytics gate — not telemetry: feeds the ranker's
   * acceptance history (ranking/acceptanceHistory.ts). Lives here because
   * this is the one place the widget's accept event is already hooked.
   */
  readonly onAccepted?: (label: string) => void;
};

export type CompletionTelemetryHandle = {
  readonly recordOrchestratorRun: (meta: { positionKind?: CompletionPositionKind }) => void;
  /**
   * Called by `SqlMonaco.tsx` after the orchestrator returns (and the
   * cancellation token has NOT fired — see recordOrchestratorSuperseded).
   * Feeds the `completion.session_summary` aggregate with the elapsed
   * latency and final suggestion count — one histogram increment per
   * `provideCompletionItems` invocation, never a per-event emit.
   */
  readonly recordOrchestratorResolved: (meta: {
    positionKind?: CompletionPositionKind;
    suggestionCount: number;
    /**
     * GRA-8 — which classifier produced `positionKind` (`'clause'` text machine
     * vs `'slot'` fine-grained gate). The which-path counter; omitted when the
     * resolver result does not surface it. Forwarded verbatim to the
     * `completion.resolved` telemetry payload.
     */
    classifiedBy?: CompletionClassifiedBy;
    /**
     * Value-slot plan T14 — the value-slot's `role` (currently only
     * `'comparison-rhs'`) when `positionKind` is `'scalar-value'`. Forwarded
     * verbatim; omitted for every other position. NEVER the LHS operand
     * text/identifier — only the low-cardinality role string travels here.
     */
    valueSlotRole?: ValueSlotRole;
    /**
     * Predicate-head plan C1 flip-gate — the predicate-head's `role`
     * (`'where-head'` | `'having-head'` | `'on-head'`) when `positionKind`
     * is `'predicate-head'`. Forwarded verbatim; omitted for every other
     * position.
     */
    predicateHeadRole?: PredicateHeadRole;
    /**
     * Synchronous main-thread ms the provider spent on this request
     * (`providerCost.ts`); undefined when not measurable. Feeds `prov_*`.
     */
    providerMs?: number;
    /** The untyped cap removed candidates (`RankReport.wasTruncated`). Feeds `truncatedCount`. */
    truncated?: boolean;
  }) => void;
  /**
   * Called instead of `recordOrchestratorResolved` when the provider's
   * cancellation token fired during the run — Monaco will discard the result
   * (the user kept typing). Keeps the latency histogram survivorship-honest.
   * `providerMs`: the main-thread time the discarded run still cost.
   */
  readonly recordOrchestratorSuperseded: (meta?: { providerMs?: number }) => void;
  readonly recordOrchestratorError: (stage: CompletionOrchestratorStage, error: unknown) => void;
  readonly dispose: () => void;
};

type RunState = {
  readonly startedAt: number;
  readonly positionKind: CompletionPositionKind;
};

type Disposable = { readonly dispose: () => void };

export function wireCompletionTelemetry(
  editor: any,
  deps: TelemetryDeps,
): CompletionTelemetryHandle {
  let lastRun: RunState | null = null;
  /**
   * Position kind of the list the widget shows: set by each resolve, cleared
   * when the widget hides and on an orchestrator error, so a list no resolve
   * produced (safe mode's word-based list, the snippets-only error fallback)
   * never inherits an earlier semantic list's kind.
   */
  let shownPositionKind: CompletionPositionKind | null = null;
  let acceptedSinceShow = false;
  let lastDismissReason: CompletionDismissReason = 'other';
  const opts = () => ({ analyticsEnabled: deps.getAnalyticsEnabled() });

  const suggestController = safeGetContribution(editor, 'editor.contrib.suggestController');
  // KNOWN LIMITATION (telemetry under-count on widget recreation):
  // The suggest widget is resolved ONCE here, at wire time, and the
  // onDidShow / onDidHide listeners below are bound to THIS
  // specific widget instance. If Monaco later recreates the suggest widget
  // (rare — e.g. the underlying editor is disposed and recreated, or a
  // contribution reset), the new widget is unhooked and its show / hide /
  // accept events are silently NOT captured → completion telemetry
  // under-counts for that editor instance until it is re-wired.
  // Lazy per-event re-resolution would NOT fix this: the event listeners are
  // bound to this instance, not re-attached per event. A correct fix
  // requires detecting widget recreation and re-wiring all listeners onto
  // the new instance (while avoiding double-wiring) — a disproportionate
  // refactor for a Minor, so it is deliberately deferred.
  const widget = safeGetWidget(suggestController);

  const disposables: Disposable[] = [];

  // Every accept path — click, Enter, Tab, commit character — runs the
  // controller's `_insertSuggestion`, which fires this BEFORE it cancels the
  // session and hides the widget. So this is where an accept is recorded:
  // the list's position kind is still set, and `acceptedSinceShow` is set in
  // time for the hide that follows not to count a dismissal. It also feeds
  // the ranker's acceptance history regardless of which widget instance
  // rendered the list.
  addDisposable(disposables, safeOn(suggestController, 'onWillInsertSuggestItem', (e: any) => {
    const item = e?.item;
    const rawLabel = item?.completion?.label ?? item?.textLabel;
    const label = typeof rawLabel === 'string' ? rawLabel : rawLabel?.label;
    if (typeof label === 'string') deps.onAccepted?.(label);
    recordAccept(item);
  }));

  function recordAccept(item: any): void {
    acceptedSinceShow = true;
    // Snapshot everything the event needs now — the hide that follows clears
    // the shown list's position kind.
    const positionKind = shownPositionKind ?? lastRun?.positionKind ?? 'unknown';
    const tier = extractTier(item?.completion);
    const rankIndex = rankIndexOf(widget, item);
    const latencyMs = lastRun ? Math.max(0, now() - lastRun.startedAt) : 0;
    if (!deps.getAnalyticsEnabled()) return;
    const engineId = deps.getEngineId();
    // Exact total always aggregates; the detailed per-event emit is capped
    // per app run (see MAX_ACCEPTED_EVENTS_PER_ENGINE_RUN above).
    recordAccepted(engineId);
    const emitted = acceptedEmittedByEngine.get(engineId) ?? 0;
    if (emitted >= MAX_ACCEPTED_EVENTS_PER_ENGINE_RUN) return;
    acceptedEmittedByEngine.set(engineId, emitted + 1);
    // matchQuality is intentionally omitted: the ranker's per-item prefix-match score is
    // not plumbed through the Monaco bridge (only `tier` via sortText survives). Emitting
    // a constant 0 would corrupt the ranking-regression signal by making every accept look
    // like a perfect exact-prefix match. The field is deferred until the "Per-Item Metadata
    // Plumbing" slice plumbs the real value end-to-end.
    emitCompletionAccepted(deps.track, { engineId, positionKind, tier, rankIndex, latencyMs }, opts());
  }

  if (widget) {
    addDisposable(disposables, safeOn(widget, 'onDidShow', () => {
      acceptedSinceShow = false;
      lastDismissReason = 'other';
    }));
    addDisposable(disposables, safeOn(widget, 'onDidHide', () => {
      shownPositionKind = null;
      // Aggregated (completion.session_summary) — the widget hides on nearly
      // every keystroke burst, so this must never be a per-event emit.
      if (!acceptedSinceShow && deps.getAnalyticsEnabled()) {
        recordDismissed(deps.getEngineId(), lastDismissReason);
      }
      acceptedSinceShow = false;
      lastDismissReason = 'other';
    }));
  }

  // Track Escape key as a more specific dismiss reason. The widget's
  // onDidHide fires after the keydown listener, so the reason is set in
  // time for the dismissed event to carry it.
  addDisposable(disposables, safeOn(editor, 'onKeyDown', (e: any) => {
    if (e?.code === 'Escape' || e?.keyCode === 9) {
      lastDismissReason = 'esc';
    }
  }));
  addDisposable(disposables, safeOn(editor, 'onDidChangeCursorPosition', () => {
    if (lastDismissReason === 'other') lastDismissReason = 'cursor-moved';
  }));

  return {
    recordOrchestratorRun: (meta) => {
      lastRun = {
        startedAt: now(),
        positionKind: meta.positionKind ?? 'unknown',
      };
      acceptedSinceShow = false;
      lastDismissReason = 'other';
    },
    recordOrchestratorResolved: (meta) => {
      // The accept that follows is attributed to this request (F4). A later
      // run that is out-typed never resolves, so it cannot re-label the list.
      shownPositionKind = meta.positionKind ?? null;
      // Aggregated (completion.session_summary): with quickSuggestions on,
      // this fires per keystroke — a per-event emit would fleet-scale to
      // 20-100x the Umami instance's daily ingest (EditorPerfTelemetry-Plan
      // P0). Gated at record time so opt-out never accumulates counters.
      if (!deps.getAnalyticsEnabled()) return;
      const latencyMs = lastRun ? Math.max(0, now() - lastRun.startedAt) : 0;
      recordResolved(deps.getEngineId(), {
        latencyMs,
        suggestionCount: meta.suggestionCount,
        classifiedBy: meta.classifiedBy,
        valueSlotRole: meta.valueSlotRole,
        predicateHeadRole: meta.predicateHeadRole,
        providerMs: meta.providerMs,
        truncated: meta.truncated,
      });
    },
    recordOrchestratorSuperseded: (meta) => {
      // The run's result is about to be discarded (Monaco cancellation token
      // fired — the user out-typed it). Counted separately so the latency
      // histogram only holds runs whose suggestions were deliverable: without
      // this, a chronically-too-slow provider looks FAST (its slow runs never
      // resolve into the histogram) — the survivorship bias P2 exists to kill.
      if (!deps.getAnalyticsEnabled()) return;
      recordSuperseded(deps.getEngineId(), meta?.providerMs);
    },
    // The caught error is deliberately NOT forwarded — error text is user
    // data and never crosses the analytics boundary (see completionTelemetry).
    recordOrchestratorError: (stage, _error) => {
      shownPositionKind = null;
      if (!deps.getAnalyticsEnabled()) return;
      const engineId = deps.getEngineId();
      // Exact per-stage total always aggregates; the per-event emit is capped
      // per (engine, stage) per app run — this path can fire per keystroke
      // under a deterministic resolver bug (volume-audit risk #1).
      recordOrchestratorErrorCount(engineId, stage);
      const capKey = `${engineId}|${stage}`;
      const emitted = errorEmittedByEngineStage.get(capKey) ?? 0;
      if (emitted >= MAX_ERROR_EVENTS_PER_ENGINE_STAGE_RUN) return;
      errorEmittedByEngineStage.set(capKey, emitted + 1);
      emitCompletionOrchestratorError(deps.track, {
        engineId,
        stage,
      }, opts());
    },
    dispose: () => {
      for (const d of disposables) {
        try { d.dispose(); } catch { /* ignore */ }
      }
    },
  };
}

function addDisposable(list: Disposable[], d: Disposable | null): void {
  if (d) list.push(d);
}

function safeGetContribution(editor: any, id: string): any {
  try {
    return editor?.getContribution?.(id) ?? null;
  } catch {
    return null;
  }
}

function safeGetWidget(controller: any): any {
  try {
    const widget = controller?.widget;
    if (!widget) return null;
    return typeof widget === 'object' && 'value' in widget ? widget.value : widget;
  } catch {
    return null;
  }
}

function safeOn(target: any, eventName: string, handler: (event: any) => void): Disposable | null {
  try {
    const fn = target?.[eventName];
    if (typeof fn !== 'function') return null;
    const disp = fn.call(target, handler);
    if (disp && typeof disp.dispose === 'function') return disp;
    return null;
  } catch {
    return null;
  }
}

/** Position of `item` in the list on screen (the widget's completion model,
 *  filtered + sorted); 0 when the widget exposes no model. */
function rankIndexOf(widget: any, item: any): number {
  try {
    const focused = widget?.getFocusedItem?.();
    const idx = focused?.model?.items?.indexOf?.(item);
    if (typeof idx === 'number' && idx >= 0) return idx;
    return focused?.item === item && typeof focused?.index === 'number' ? focused.index : 0;
  } catch {
    return 0;
  }
}

function extractTier(item: any): number {
  const sortText: unknown = item?.sortText;
  if (typeof sortText !== 'string') return 0;
  const m = /^(\d)_/.exec(sortText);
  return m ? Number(m[1]) : 0;
}

function now(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}
